import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MetaTags from "react-meta-tags";
import { withRouter } from "react-router-dom";
import Tooltip from "@material-ui/core/Tooltip";
import Select from "react-select";

import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
} from "reactstrap";

import BootstrapTable from "react-bootstrap-table-next";

import Breadcrumbs from "components/Common/Breadcrumb";

import { getSchemelist, COACriterialist } from "store/scheme/actions";
import { getcyclelist } from "store/cycle/actions";
import { isEmpty } from "lodash";
import "assets/scss/table.scss";
import {
  submitCOAParticipants,
  getCOASubmittedList, // ← NEW: action to fetch previously saved COA criteria/status
} from "store/scheme/actions";

/* ─────────────────────────── helpers ─────────────────────────── */
const extractRoundNum = roundNo => {
  try {
    const part = String(roundNo).split("-").pop();
    const m = part.match(/^0\d{1,2}(\d+)$/);
    if (m) return parseInt(m[1]);
    return parseInt(part.replace(/\D/g, "")) || 0;
  } catch {
    return 0;
  }
};

const getFailedAnalytesCount = analytes => {
  let failedCount = 0;
  (analytes || []).forEach(analyte => {
    const rejectedRounds = (analyte.round_details || [])
      .filter(r => r.evaluation === "Rejected")
      .map(r => extractRoundNum(r.round_no))
      .sort((a, b) => a - b);

    if (rejectedRounds.length < 3) return;

    let maxStreak = 1;
    let currentStreak = 1;
    for (let i = 1; i < rejectedRounds.length; i++) {
      if (rejectedRounds[i] === rejectedRounds[i - 1] + 1) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 1;
      }
    }
    if (maxStreak >= 3) failedCount++;
  });
  return failedCount;
};

const getLongestConsecutiveStreak = sortedRejectedRounds => {
  if (!sortedRejectedRounds.length) return [];
  let bestStreak = [];
  let currentStreak = [sortedRejectedRounds[0]];

  for (let i = 1; i < sortedRejectedRounds.length; i++) {
    const prev = extractRoundNum(sortedRejectedRounds[i - 1].round_no);
    const curr = extractRoundNum(sortedRejectedRounds[i].round_no);
    if (curr === prev + 1) {
      currentStreak.push(sortedRejectedRounds[i]);
    } else {
      if (currentStreak.length > bestStreak.length) bestStreak = currentStreak;
      currentStreak = [sortedRejectedRounds[i]];
    }
  }
  if (currentStreak.length > bestStreak.length) bestStreak = currentStreak;
  return bestStreak;
};

/* ── Column Filter Input Component ── */
const ColumnFilter = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder || "Filter..."}
    onClick={e => e.stopPropagation()}
    style={{
      width: "100%",
      marginTop: "5px",
      padding: "5px 9px",
      fontSize: "12px",
      border: "1.5px solid #b0cfe8",
      borderRadius: "6px",
      outline: "none",
      background: "#ffffff",
      color: "#333",
      boxSizing: "border-box",
      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
      fontFamily: "inherit",
    }}
  />
);

ColumnFilter.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

ColumnFilter.defaultProps = {
  placeholder: "Filter...",
};

/* ──────────────────────────────────────────────────────────────── */

class ReagentsList extends Component {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      nameFilter: "",
      schemeTypeFilter: "",
      schemeNameFilter: "",
      cycleRoundsCount: 0,
      cycleNameFilter: "",
      roundFilter: "",
      analyteRejectionFilter: "",
      roundOptions: [],
      selectedSchemeType: "",
      summaryModalOpen: false,
      /* ── top-level dropdown filters (matching Payment page style) ── */
      topFilter_labName: "",
      topFilter_coaStatus: "",
      topFilter_roundsParticipated: "",

      /* ── per-column table filters ── */
      colFilter_labName: "",
      colFilter_roundsIssued: "",
      colFilter_roundsParticipated: "",
      colFilter_consecutiveRejections: "",
      colFilter_coaStatus: "",

      /* analyte-rejection modal */
      analytesModalOpen: false,
      selectedParticipantAnalytes: [],
      selectedParticipantName: "",

      /* change-status modal */
      changeStatusModalOpen: false,
      selectedParticipantForStatus: null,
      newStatusValue: "",
      statusChangeReason: "",
      manualStatusOverrides: {},

      /* submit */
      isSubmitting: false,
      feedbackMessage: "",

      /* loading */
      isLoadingParticipants: false,

      user_id: localStorage.getItem("authUser")
        ? JSON.parse(localStorage.getItem("authUser")).user_id
        : "",
    };
  }

  /* ── lifecycle ── */
  componentDidMount() {
    this.props.onGetScheme(this.state.user_id);
    this.props.onGetCycle(this.state.user_id);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.coaData !== this.props.coaData) {
      const coaData = this.props.coaData || [];
      const { schemeType } = this.props;
      if (coaData.length > 0) {
        const cycles = coaData[0]?.cycles || [];
        const allRounds = cycles.flatMap(c => c.rounds || []);
        const roundsCount = cycles.length > 0 ? cycles[0].rounds_count : 0;
        this.setState({
          roundOptions: allRounds,
          cycleRoundsCount: roundsCount,
          selectedSchemeType: schemeType,
          isLoadingParticipants: false, // ← data has arrived, stop loading
        });
      } else {
        // coaData is empty (e.g. no results), also stop loading
        this.setState({ isLoadingParticipants: false });
      }
    }

    if (
      !isEmpty(this.props.COACriteria) &&
      this.props.COACriteria !== prevProps.COACriteria
    ) {
      if (!this.state.originalSchemes) {
        this.setState({ originalSchemes: this.props.COACriteria });
      }
    }

    // ← NEW: rehydrate criteria + manual overrides once saved COA data arrives
    if (prevProps.submittedCOAList !== this.props.submittedCOAList) {
      const saved = this.props.submittedCOAList || [];

      if (saved.length > 0) {
        // All rows for a given scheme+cycle were saved with the same criteria
        const first = saved[0];
        const rebuiltOverrides = {};
        saved.forEach(row => {
          rebuiltOverrides[row.participant_id] = row.coa_status === "Eligible";
        });

        this.setState({
          roundFilter: first.criteria_min_rounds
            ? String(first.criteria_min_rounds)
            : "",
          analyteRejectionFilter: first.criteria_max_rejections
            ? String(first.criteria_max_rejections)
            : "",
          manualStatusOverrides: rebuiltOverrides,
        });
      } else {
        // No prior submission found for this scheme+cycle — reset to blank state
        this.setState({
          roundFilter: "",
          analyteRejectionFilter: "",
          manualStatusOverrides: {},
        });
      }
    }

    const { coaSubmit } = this.props;
    const prev = prevProps.coaSubmit;

    if (coaSubmit.success && !prev.success) {
      this.setState({
        feedbackMessage: `✅ ${
          this.buildParticipants().length
        } participant(s) submitted successfully.`,
      });
      setTimeout(() => this.setState({ feedbackMessage: "" }), 4000);
    }

    if (coaSubmit.error && !prev.error) {
      this.setState({
        feedbackMessage: `❌ Submission failed: ${coaSubmit.error}`,
      });
      setTimeout(() => this.setState({ feedbackMessage: "" }), 4000);
    }
  }

  /* ── status modal ── */
  openChangeStatusModal = row => {
    const override = this.state.manualStatusOverrides;
    const hasOverride = override.hasOwnProperty(row.lab_id);
    const currentStatus = hasOverride ? override[row.lab_id] : row.coa_eligible;
    this.setState({
      changeStatusModalOpen: true,
      selectedParticipantForStatus: row,
      newStatusValue: currentStatus ? "eligible" : "not_eligible",
      statusChangeReason: "",
    });
  };

  handleStatusChange = () => {
    const { selectedParticipantForStatus, newStatusValue, statusChangeReason } =
      this.state;

    const updatedOverrides = {
      ...this.state.manualStatusOverrides,
      [selectedParticipantForStatus.lab_id]: newStatusValue === "eligible",
    };
    this.setState({
      manualStatusOverrides: updatedOverrides,
      changeStatusModalOpen: false,
    });

    // Get scheme and cycle details
    const { schemeList, cycleList } = this.props;
    const schemeObj = (schemeList || []).find(
      s => String(s.id) === String(this.state.schemeNameFilter)
    );
    const cycleObj = (cycleList || []).find(
      c => String(c.id) === String(this.state.cycleNameFilter)
    );

    const payload = {
      participants: [
        {
          participant_id: selectedParticipantForStatus.lab_id,
          participant_name: selectedParticipantForStatus.lab_name,
          scheme_id: this.state.schemeNameFilter,
          scheme_name: schemeObj?.name || "",
          cycle_id: this.state.cycleNameFilter,
          cycle_no: cycleObj?.cycle_no || "",
          rounds_participated:
            selectedParticipantForStatus.rounds_participated || 0,
          total_rejections: getFailedAnalytesCount(
            selectedParticipantForStatus.analytes || []
          ),
          coa_status:
            newStatusValue === "eligible" ? "Eligible" : "Not Eligible",
          criteria_min_rounds: this.state.roundFilter
            ? Number(this.state.roundFilter)
            : null,
          criteria_max_rejections: this.state.analyteRejectionFilter
            ? Number(this.state.analyteRejectionFilter)
            : null,
        },
      ],
    };

    this.props.onSubmitCOA(payload);
  };
  buildParticipants = () => {
    const selectedRounds = Number(this.state.roundFilter);
    const rejectionLimit = Number(this.state.analyteRejectionFilter);

    const raw =
      this.props.coaData?.flatMap(
        scheme =>
          scheme.cycles?.flatMap(cycle =>
            (cycle.participants_summary || []).map(p => ({
              ...p,
              coa_eligible: selectedRounds
                ? p.rounds_participated >= selectedRounds
                : p.coa_eligible,
            }))
          ) || []
      ) || [];

    raw.sort((a, b) => (a.lab_name || "").localeCompare(b.lab_name || ""));

    return raw.map(p => {
      let isEligible = true;
      if (selectedRounds && p.rounds_participated < selectedRounds)
        isEligible = false;
      if (isEligible && rejectionLimit > 0) {
        if (getFailedAnalytesCount(p.analytes) >= rejectionLimit)
          isEligible = false;
      }
      const hasOverride = this.state.manualStatusOverrides.hasOwnProperty(
        p.lab_id
      );
      const finalEligible = hasOverride
        ? this.state.manualStatusOverrides[p.lab_id]
        : isEligible;
      return { ...p, coa_eligible: finalEligible };
    });
  };

  /* ── Unique options for top-level dropdowns ── */
  getUniqueLabNames = participants =>
    [
      ...new Set((participants || []).map(p => p.lab_name).filter(Boolean)),
    ].sort();

  /* ── Apply TOP-LEVEL dropdown filters ── */
  applyTopFilters = participants => {
    const {
      topFilter_labName,
      topFilter_coaStatus,
      topFilter_roundsParticipated,
    } = this.state;
    const hasCriteria =
      this.state.roundFilter || this.state.analyteRejectionFilter;

    return participants.filter(p => {
      // Lab Name dropdown
      if (topFilter_labName && topFilter_labName !== "All") {
        if ((p.lab_name || "") !== topFilter_labName) return false;
      }

      // COA Status dropdown
      if (topFilter_coaStatus && topFilter_coaStatus !== "All") {
        if (!hasCriteria) return true;
        const override = this.state.manualStatusOverrides;
        const hasOverride = override.hasOwnProperty(p.lab_id);
        const displayValue = hasOverride ? override[p.lab_id] : p.coa_eligible;
        const statusText = displayValue ? "Eligible" : "Not Eligible";
        if (statusText !== topFilter_coaStatus) return false;
      }

      // Rounds Participated dropdown
      if (
        topFilter_roundsParticipated &&
        topFilter_roundsParticipated !== "All"
      ) {
        if (
          String(p.rounds_participated || "") !== topFilter_roundsParticipated
        )
          return false;
      }

      return true;
    });
  };

  /* ── Apply per-column filters to built participants ── */
  applyColumnFilters = participants => {
    const {
      colFilter_labName,
      colFilter_roundsIssued,
      colFilter_roundsParticipated,
      colFilter_consecutiveRejections,
      colFilter_coaStatus,
    } = this.state;

    return participants.filter(p => {
      if (
        colFilter_labName &&
        !(p.lab_name || "")
          .toLowerCase()
          .includes(colFilter_labName.toLowerCase())
      )
        return false;

      if (
        colFilter_roundsIssued &&
        !String(p.rounds_issued || "").includes(colFilter_roundsIssued)
      )
        return false;

      if (
        colFilter_roundsParticipated &&
        !String(p.rounds_participated || "").includes(
          colFilter_roundsParticipated
        )
      )
        return false;

      if (colFilter_consecutiveRejections) {
        const count = String(getFailedAnalytesCount(p.analytes || []));
        if (!count.includes(colFilter_consecutiveRejections)) return false;
      }

      if (colFilter_coaStatus) {
        const hasCriteria =
          this.state.roundFilter || this.state.analyteRejectionFilter;
        let statusText = "—";
        if (hasCriteria) {
          const override = this.state.manualStatusOverrides;
          const hasOverride = override.hasOwnProperty(p.lab_id);
          const displayValue = hasOverride
            ? override[p.lab_id]
            : p.coa_eligible;
          statusText = displayValue ? "eligible" : "not eligible";
        }
        if (!statusText.includes(colFilter_coaStatus.toLowerCase()))
          return false;
      }

      return true;
    });
  };

  handleSubmit = () => {
    const participants = this.buildParticipants();
    if (!participants.length) return;
    this.setState({ summaryModalOpen: true });
  };

  confirmSubmit = () => {
    const {
      schemeNameFilter,
      cycleNameFilter,
      roundFilter,
      analyteRejectionFilter,
    } = this.state;
    const { schemeList, cycleList } = this.props;
    const participants = this.buildParticipants();

    const schemeObj = (schemeList || []).find(
      s => String(s.id) === String(schemeNameFilter)
    );
    const cycleObj = (cycleList || []).find(
      c => String(c.id) === String(cycleNameFilter)
    );

    const payload = {
      participants: participants.map(p => ({
        participant_id: p.lab_id,
        participant_name: p.lab_name,
        scheme_id: schemeNameFilter,
        scheme_name: schemeObj?.name || "",
        cycle_id: cycleNameFilter,
        cycle_no: cycleObj?.cycle_no || "",
        rounds_issued: p.rounds_issued,
        rounds_participated: p.rounds_participated,
        total_rejections: getFailedAnalytesCount(p.analytes || []),
        coa_status: p.coa_eligible ? "Eligible" : "Not Eligible",
        criteria_min_rounds: roundFilter ? Number(roundFilter) : null,
        criteria_max_rejections: analyteRejectionFilter
          ? Number(analyteRejectionFilter)
          : null,
      })),
    };

    this.setState({ summaryModalOpen: false });
    this.props.onSubmitCOA(payload);
  };

  /* ── Build columns dynamically — receives hasCriteria from render() ── */
  buildColumns = hasCriteria => {
    const thSkyBlue = {
      backgroundColor: "#87ceeb",
      color: "#1a1a2e",
      textAlign: "center",
      verticalAlign: "top",
      padding: "10px 8px 8px 8px",
      borderRight: "1px solid #6bb8d8",
      borderBottom: "2px solid #5aaac8",
      whiteSpace: "normal",
    };

    const thYellow = {
      backgroundColor: "#fff9c4",
      color: "#1a1a2e",
      textAlign: "center",
      verticalAlign: "top",
      padding: "10px 8px 8px 8px",
      borderRight: "1px solid #e6d96a",
      borderBottom: "2px solid #d4c840",
      whiteSpace: "normal",
    };

    const thLightBlue = {
      backgroundColor: "#d0e2ff",
      color: "#1a1a2e",
      textAlign: "center",
      verticalAlign: "top",
      padding: "10px 8px 8px 8px",
      borderRight: "1px solid #a8c8f8",
      borderBottom: "2px solid #8ab4f0",
      whiteSpace: "normal",
    };

    const cellSkyBlue = { backgroundColor: "#f0f9ff", textAlign: "left" };
    const cellYellow = { backgroundColor: "#fffef0", textAlign: "center" };
    const cellLightBlue = { backgroundColor: "#f0f5ff", textAlign: "center" };

    /* Reusable header renderer */
    const makeHeader = (
      text,
      filterVal,
      filterKey,
      placeholder,
      sortEl,
      thStyle
    ) => (
      <div
        style={{ display: "flex", flexDirection: "column", minWidth: "80px" }}
      >
        <span
          style={{
            fontWeight: "500",
            fontSize: "13px",
            color: "#1c2a3a",
            whiteSpace: "normal",
            lineHeight: "1.4",
            marginBottom: "2px",
          }}
        >
          {text} {sortEl}
        </span>
        <ColumnFilter
          value={filterVal}
          onChange={val => this.setState({ [filterKey]: val })}
          placeholder={placeholder}
        />
      </div>
    );

    return [
      /* ── Sky Blue group: Participant info ── */
      {
        dataField: "lab_name",
        text: "Participant Name",
        sort: true,
        headerStyle: thSkyBlue,
        style: cellSkyBlue,
        headerFormatter: (col, _i, { sortElement }) =>
          makeHeader(
            col.text,
            this.state.colFilter_labName,
            "colFilter_labName",
            "Enter Participant...",
            sortElement,
            thSkyBlue
          ),
      },
      /* ── Yellow group: Numeric counts ── */
      {
        dataField: "rounds_issued",
        text: "Rounds Issued",
        sort: true,
        headerStyle: thYellow,
        style: cellYellow,
        headerFormatter: (col, _i, { sortElement }) =>
          makeHeader(
            col.text,
            this.state.colFilter_roundsIssued,
            "colFilter_roundsIssued",
            "Enter Rounds...",
            sortElement,
            thYellow
          ),
      },
      {
        dataField: "rounds_participated",
        text: "Rounds Submitted",
        sort: true,
        headerStyle: thYellow,
        style: cellYellow,
        headerFormatter: (col, _i, { sortElement }) =>
          makeHeader(
            col.text,
            this.state.colFilter_roundsParticipated,
            "colFilter_roundsParticipated",
            "Enter Rounds...",
            sortElement,
            thYellow
          ),
      },
      {
        dataField: "total_rejections",
        text: "Consecutive Rejections",
        sort: true,
        headerStyle: thYellow,
        style: cellYellow,
        headerFormatter: (col, _i, { sortElement }) =>
          makeHeader(
            col.text,
            this.state.colFilter_consecutiveRejections,
            "colFilter_consecutiveRejections",
            "Enter Count...",
            sortElement,
            thYellow
          ),
        formatter: (cell, row) => {
          const consecutiveCount = getFailedAnalytesCount(row.analytes || []);
          return (
            <span
              onClick={() => {
                if (consecutiveCount > 0) {
                  this.setState({
                    analytesModalOpen: true,
                    selectedParticipantAnalytes: row.analytes || [],
                    selectedParticipantName: row.lab_name || "",
                    selectedSchemeType: this.state.schemeTypeFilter || "",
                  });
                }
              }}
              style={{
                color: consecutiveCount > 0 ? "red" : "inherit",
                fontWeight: consecutiveCount > 0 ? "bold" : "normal",
                cursor: consecutiveCount > 0 ? "pointer" : "default",
                textDecoration: consecutiveCount > 0 ? "underline" : "none",
              }}
            >
              {consecutiveCount}
            </span>
          );
        },
      },

      {
        dataField: "coa_eligible",
        text: "COA Status",
        sort: true,
        headerStyle: thLightBlue,
        style: cellLightBlue,
        headerFormatter: (col, _i, { sortElement }) =>
          makeHeader(
            col.text,
            this.state.colFilter_coaStatus,
            "colFilter_coaStatus",
            "Eligible / Not...",
            sortElement,
            thLightBlue
          ),

        formatter: (cell, row) => {
          const override = this.state.manualStatusOverrides;
          const hasOverride = override.hasOwnProperty(row.lab_id);
          const displayValue = hasOverride ? override[row.lab_id] : cell;

          return (
            <span
              style={{
                color: displayValue ? "green" : "red",
                fontWeight: "bold",
              }}
            >
              {displayValue ? "Eligible" : "Not Eligible"}

              {hasOverride && (
                <span
                  title="Manually overridden"
                  style={{
                    marginLeft: "6px",
                    fontSize: "11px",
                    color: "#888",
                    fontWeight: "normal",
                  }}
                >
                  (Manual)
                </span>
              )}
            </span>
          );
        },
      },
      {
        dataField: "action",
        text: "Action",
        sort: false,
        headerStyle: { ...thLightBlue, borderRight: "none" },
        style: { ...cellLightBlue, textAlign: "center" },
        headerFormatter: col => (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{ fontWeight: "700", fontSize: "13px", color: "#1a1a2e" }}
            >
              {col.text}
            </span>
            <div style={{ height: "33px", marginTop: "6px" }} />
          </div>
        ),
        formatter: (cell, row) => {
          return (
            <Tooltip
              title={hasCriteria ? "Change COA Status" : "Set criteria first"}
              placement="top"
            >
              <button
                onClick={() => hasCriteria && this.openChangeStatusModal(row)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: hasCriteria ? "pointer" : "not-allowed",
                  opacity: hasCriteria ? 1 : 0.3,
                  padding: "4px 8px",
                  borderRadius: "6px",
                }}
              >
                <i
                  className="mdi mdi-pencil font-size-18"
                  style={{ fontSize: "22px", color: "#556ee6" }}
                />
              </button>
            </Tooltip>
          );
        },
      },
    ];
  };

  render() {
    const schemeList = this.props.schemeList || [];

    const filteredSchemesForDropdown = schemeList.filter(s => {
      if (!this.state.schemeTypeFilter) return true;
      return (
        s.analytetype?.toLowerCase() ===
        this.state.schemeTypeFilter.toLowerCase()
      );
    });

    const builtParticipants = !this.state.cycleNameFilter
      ? []
      : this.buildParticipants();

    // Apply top-level filters first, then column filters
    const topFilteredParticipants = this.applyTopFilters(builtParticipants);
    const filteredParticipants = this.applyColumnFilters(
      topFilteredParticipants
    );

    const schemeNameOptions = filteredSchemesForDropdown.map(s => ({
      value: s.id,
      label: s.name,
    }));

    const cycleOptions = (this.props.cycleList || []).filter(cycle => {
      if (!this.state.schemeNameFilter) return false;
      const matchesScheme =
        String(cycle.scheme_id) === String(this.state.schemeNameFilter);
      const isOld = cycle.reportstatus === "Old"; // ✅ only Old cycles
      return matchesScheme && isOld;
    });
    const currentParticipant = this.state.selectedParticipantForStatus;

    const modalAnalytes = this.state.selectedParticipantAnalytes.filter(a => {
      const rejectedNums = (a.round_details || [])
        .filter(rd => rd.evaluation === "Rejected")
        .map(rd => extractRoundNum(rd.round_no))
        .sort((x, y) => x - y);

      if (rejectedNums.length < 3) return false;

      let maxStreak = 1;
      let current = 1;
      for (let i = 1; i < rejectedNums.length; i++) {
        if (rejectedNums[i] === rejectedNums[i - 1] + 1) {
          current++;
          maxStreak = Math.max(maxStreak, current);
        } else {
          current = 1;
        }
      }
      return maxStreak >= 3;
    });

    // ── derive hasCriteria ONCE here in render() and pass to buildColumns ──
    const hasCriteria = !!(
      this.state.roundFilter || this.state.analyteRejectionFilter
    );

    const columns = this.buildColumns(hasCriteria);

    // Unique options for top-level dropdowns
    const uniqueLabNames = this.getUniqueLabNames(builtParticipants);
    const uniqueRoundsParticipated = [
      ...new Set(
        builtParticipants
          .map(p => String(p.rounds_participated || ""))
          .filter(Boolean)
      ),
    ].sort((a, b) => Number(a) - Number(b));

    return (
      <React.Fragment>
        {/* ── COA Summary Modal ── */}
        <Modal
          isOpen={this.state.summaryModalOpen}
          toggle={() => this.setState({ summaryModalOpen: false })}
          size="xl"
          centered
        >
          <ModalHeader
            toggle={() => this.setState({ summaryModalOpen: false })}
          >
            <span style={{ color: "#556ee6", fontWeight: "bold" }}>
              COA Submission Summary
            </span>
          </ModalHeader>

          <ModalBody style={{ padding: "24px" }}>
            {(() => {
              const all = this.buildParticipants();
              const eligible = all.filter(p => p.coa_eligible);
              const notEligible = all.filter(p => !p.coa_eligible);

              return (
                <>
                  {/* ── Stats row ── */}
                  <Row className="mb-4">
                    {[
                      { label: "Total Labs", value: all.length, color: "#333" },
                      {
                        label: "Eligible",
                        value: eligible.length,
                        color: "#1a7d47",
                      },
                      {
                        label: "Not Eligible",
                        value: notEligible.length,
                        color: "#c0392b",
                      },
                    ].map(({ label, value, color }) => (
                      <Col key={label} md={4}>
                        <div
                          style={{
                            background: "#f8f9fa",
                            borderRadius: "10px",
                            padding: "14px",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#888",
                              marginBottom: "6px",
                            }}
                          >
                            {label}
                          </div>
                          <div
                            style={{
                              fontSize: "26px",
                              fontWeight: "600",
                              color,
                            }}
                          >
                            {value}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  {/* ── Two tables side by side ── */}
                  <Row>
                    {/* Eligible */}
                    <Col md={6}>
                      <div
                        style={{
                          border: "0.5px solid #c3e6cb",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            background: "#eaf4ee",
                            padding: "9px 14px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#1a7d47",
                            borderBottom: "0.5px solid #c3e6cb",
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#27ae60",
                              display: "inline-block",
                            }}
                          />
                          Eligible Labs ({eligible.length})
                        </div>
                        <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                          {eligible.length === 0 ? (
                            <p
                              className="text-center text-muted py-3"
                              style={{ fontSize: "13px" }}
                            >
                              None
                            </p>
                          ) : (
                            eligible.map(p => (
                              <div
                                key={p.lab_id}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "8px 14px",
                                  borderBottom: "0.5px solid #f0f0f0",
                                  fontSize: "13px",
                                }}
                              >
                                <span style={{ color: "#333" }}>
                                  {p.lab_name}
                                </span>
                                <span
                                  style={{
                                    background: "#eaf4ee",
                                    color: "#1a7d47",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    padding: "2px 10px",
                                    borderRadius: "20px",
                                  }}
                                >
                                  Eligible
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </Col>

                    {/* Not Eligible */}
                    <Col md={6}>
                      <div
                        style={{
                          border: "0.5px solid #f5c6cb",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            background: "#fdf0ef",
                            padding: "9px 14px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#c0392b",
                            borderBottom: "0.5px solid #f5c6cb",
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#e74c3c",
                              display: "inline-block",
                            }}
                          />
                          Not Eligible Labs ({notEligible.length})
                        </div>
                        <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                          {notEligible.length === 0 ? (
                            <p
                              className="text-center text-muted py-3"
                              style={{ fontSize: "13px" }}
                            >
                              None
                            </p>
                          ) : (
                            notEligible.map(p => (
                              <div
                                key={p.lab_id}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "8px 14px",
                                  borderBottom: "0.5px solid #f0f0f0",
                                  fontSize: "13px",
                                }}
                              >
                                <span style={{ color: "#333" }}>
                                  {p.lab_name}
                                </span>
                                <span
                                  style={{
                                    background: "#fdf0ef",
                                    color: "#c0392b",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    padding: "2px 10px",
                                    borderRadius: "20px",
                                  }}
                                >
                                  Not Eligible
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </>
              );
            })()}
          </ModalBody>

          <ModalFooter
            style={{ borderTop: "1px solid #eee", padding: "12px 24px" }}
          >
            <Button
              color="light"
              onClick={() => this.setState({ summaryModalOpen: false })}
              style={{ borderRadius: "8px", padding: "8px 20px" }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={this.confirmSubmit}
              disabled={this.state.isSubmitting}
              style={{
                borderRadius: "8px",
                padding: "8px 24px",
                background: "#556ee6",
                border: "none",
              }}
            >
              <i className="mdi mdi-check me-1" />
              Confirm &amp; Submit
            </Button>
          </ModalFooter>
        </Modal>
        <style>{`
          .participants-table thead tr th {
            border-top: none !important;
          }
          .participants-table table {
            border-collapse: separate;
            border-spacing: 0;
          }
          .participants-table .table-striped tbody tr:nth-of-type(odd) {
            background-color: #f5faff;
          }
          .participants-table thead tr th input::placeholder {
            color: #8ab0cc;
            font-size: 11.5px;
          }
          .participants-table thead tr th input:focus {
            border-color: #556ee6 !important;
            box-shadow: 0 0 0 2px rgba(85,110,230,0.15);
          }
          .coa-legend-dot {
            display: inline-block;
            width: 12px;
            height: 12px;
            border-radius: 2px;
            margin-right: 5px;
            vertical-align: middle;
          }
        `}</style>

        <div className="page-content">
          <MetaTags>
            <title>Scheme List | NEQAS</title>
          </MetaTags>
          <Container fluid>
            {this.state.feedbackMessage && (
              <div
                className={`alert ${
                  this.state.feedbackMessage.includes("❌")
                    ? "alert-danger"
                    : "alert-success"
                } text-center`}
              >
                {this.state.feedbackMessage}
              </div>
            )}

            <Breadcrumbs title="Scheme" breadcrumbItem="Scheme List" />

            {/* ── filters row 1 ── */}
            <Row className="mb-3 ms-5">
              <Col md={3}>
                <Label>Scheme Type</Label>
                <Input
                  type="select"
                  value={this.state.schemeTypeFilter}
                  onChange={e =>
                    this.setState({
                      schemeTypeFilter: e.target.value,
                      schemeNameFilter: "",
                    })
                  }
                >
                  <option value="">All</option>
                  <option value="Qualitative">Qualitative</option>
                  <option value="Quantitative">Quantitative</option>
                  <option value="Identification">Identification</option>
                </Input>
              </Col>

              <Col md={3}>
                <Label>Scheme Name</Label>
                <Input
                  type="select"
                  value={this.state.schemeNameFilter}
                  onChange={e =>
                    this.setState({
                      schemeNameFilter: e.target.value,
                      cycleNameFilter: "",
                    })
                  }
                >
                  <option value="">All</option>
                  {schemeNameOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={3}>
                <Label>Cycle</Label>
                <Input
                  type="select"
                  value={this.state.cycleNameFilter}
                  onChange={e => {
                    const cycle_id = e.target.value;
                    const selectedCycle = (this.props.cycleList || []).find(
                      c => String(c.id) === String(cycle_id)
                    );
                    this.setState(
                      {
                        cycleNameFilter: cycle_id,
                        // Show spinner only when a real cycle is selected
                        isLoadingParticipants: !!cycle_id,
                        roundFilter: "",
                        roundOptions: [],
                        manualStatusOverrides: {},
                        selectedSchemeType: "",
                        colFilter_labName: "",
                        colFilter_roundsIssued: "",
                        colFilter_roundsParticipated: "",
                        colFilter_consecutiveRejections: "",
                        colFilter_coaStatus: "",
                        topFilter_labName: "",
                        topFilter_coaStatus: "",
                        topFilter_roundsParticipated: "",
                      },
                      () => {
                        if (
                          this.state.schemeNameFilter &&
                          cycle_id &&
                          selectedCycle
                        ) {
                          this.props.onGetCOACriteria({
                            id: this.state.user_id,
                            scheme_id: this.state.schemeNameFilter,
                            cycle_no: selectedCycle.cycle_no,
                          });

                          // ← NEW: fetch previously saved criteria/status for this scheme+cycle
                          this.props.onGetSubmittedCOA({
                            scheme_id: this.state.schemeNameFilter,
                            cycle_id: cycle_id,
                          });
                        } else {
                          // No valid cycle selected — stop loading immediately
                          this.setState({ isLoadingParticipants: false });
                        }
                      }
                    );
                  }}
                >
                  <option value="">All</option>
                  {cycleOptions.map(cycle => {
                    const cycleData = (this.props.coaData || [])
                      .flatMap(s => s.cycles || [])
                      .find(c => String(c.id) === String(cycle.id));

                    const dateRange =
                      cycleData?.first_round_date && cycleData?.last_round_date
                        ? ` (${cycleData.first_round_date} to ${cycleData.last_round_date})`
                        : "";

                    return (
                      <option key={cycle.id} value={cycle.id}>
                        {cycle.cycle_no}
                        {dateRange}
                      </option>
                    );
                  })}
                </Input>
              </Col>
            </Row>

            {/* ── Evaluation Criteria ── */}
            {this.state.cycleRoundsCount > 0 && (
              <Row className="mb-3">
                <Col md={12}>
                  <div
                    style={{
                      background: "#fff",
                      borderLeft: "4px solid #556ee6",
                      borderTop: "0.5px solid #dde3f8",
                      borderRight: "0.5px solid #dde3f8",
                      borderBottom: "0.5px solid #dde3f8",
                      borderRadius: "0 10px 10px 0",
                      padding: "16px 22px",
                      marginLeft: "2rem",
                      marginRight: "1rem",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#556ee6",
                        marginBottom: "10px",
                      }}
                    >
                      Evaluation Criteria
                    </div>
                    <div
                      style={{
                        background: "#f4f6fb",
                        borderRadius: "8px",
                        padding: "10px 14px",
                        fontSize: "12.5px",
                        color: "#555",
                        lineHeight: "1.8",
                        marginBottom: "16px",
                      }}
                    >
                      <div>
                        <strong>Rounds:</strong> Minimum rounds a participant
                        must attend to qualify.
                      </div>
                      <div>
                        <strong>Rejections:</strong> Max analytes allowed to
                        fail 3 rounds in a row.
                      </div>
                      <div
                        style={{
                          marginTop: "4px",
                          fontStyle: "italic",
                          color: "#777",
                        }}
                      >
                        Participants not meeting both criteria will be marked{" "}
                        <strong
                          style={{ color: "#dc3545", fontStyle: "normal" }}
                        >
                          Not Eligible
                        </strong>
                        .
                      </div>
                    </div>
                    <Row>
                      <Col md={4}>
                        <Label
                          style={{
                            fontWeight: "600",
                            fontSize: "12.5px",
                            color: "#444",
                            marginBottom: "5px",
                          }}
                        >
                          Minimum Rounds Participated
                        </Label>
                        <Input
                          type="select"
                          value={this.state.roundFilter}
                          onChange={e =>
                            this.setState({
                              roundFilter: e.target.value,
                              manualStatusOverrides: {}, // ← ye line add karni hai
                            })
                          }
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #d0d8f0",
                            fontSize: "13px",
                          }}
                        >
                          <option value="">All Rounds</option>
                          {Array.from(
                            { length: this.state.cycleRoundsCount },
                            (_, i) => i + 1
                          ).map(num => (
                            <option key={num} value={num}>
                              {num}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={4}>
                        <Label
                          style={{
                            fontWeight: "600",
                            fontSize: "12.5px",
                            color: "#444",
                            marginBottom: "5px",
                          }}
                        >
                          Max Analytes with ≥ 3 Consecutive Rejections
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          value={this.state.analyteRejectionFilter}
                          placeholder="Enter limit"
                          onChange={e =>
                            this.setState({
                              analyteRejectionFilter: e.target.value,
                              manualStatusOverrides: {}, // ← ye line add karni hai
                            })
                          }
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #d0d8f0",
                            fontSize: "13px",
                          }}
                        />
                      </Col>
                      <Col
                        md={4}
                        className="d-flex align-items-end"
                        style={{ paddingBottom: "2px" }}
                      >
                        <div className="d-flex flex-wrap gap-2">
                          {this.state.roundFilter && (
                            <span
                              style={{
                                background: "#556ee6",
                                color: "#fff",
                                borderRadius: "20px",
                                padding: "4px 12px",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              ✓ Min rounds: {this.state.roundFilter}
                            </span>
                          )}
                          {this.state.analyteRejectionFilter && (
                            <span
                              style={{
                                background: "#dc3545",
                                color: "#fff",
                                borderRadius: "20px",
                                padding: "4px 12px",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              ✓ Max rejections:{" "}
                              {this.state.analyteRejectionFilter}
                            </span>
                          )}
                          {!this.state.roundFilter &&
                            !this.state.analyteRejectionFilter && (
                              <span
                                style={{
                                  color: "#aaa",
                                  fontSize: "12px",
                                  fontStyle: "italic",
                                }}
                              >
                                No criteria set — all participants eligible
                              </span>
                            )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            )}

            {/* ── Table card ── */}
            <Row className="mt-4">
              <Col lg={12}>
                <Card>
                  <CardBody>
                    {/* ── Table header row ── */}
                    <div
                      className="d-flex align-items-center justify-content-between mb-3"
                      style={{ flexWrap: "wrap", gap: "10px" }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <h4 className="mb-0">Participants List</h4>
                      </div>

                      {builtParticipants.length > 0 && (
                        <Button
                          color="primary"
                          disabled={this.state.isSubmitting}
                          onClick={this.handleSubmit}
                          style={{
                            background: "#556ee6",
                            border: "none",
                            borderRadius: "8px",
                            padding: "8px 22px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {this.state.isSubmitting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              />
                              &nbsp;Submitting...
                            </>
                          ) : (
                            <>&nbsp;Submit </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* ── Content area: no cycle → prompt; loading → spinner; empty → no-match; else → table ── */}
                    {!this.state.cycleNameFilter ? (
                      <div className="text-center text-muted py-4">
                        <i
                          className="mdi mdi-filter-outline"
                          style={{ fontSize: "2rem" }}
                        />
                        <p className="mt-2">
                          Please select a Scheme and Cycle to view participants.
                        </p>
                      </div>
                    ) : this.state.isLoadingParticipants ? (
                      <div className="text-center py-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                          style={{ width: "2.8rem", height: "2.8rem" }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p
                          className="mt-3 text-muted"
                          style={{ fontSize: "14px" }}
                        >
                          Loading participants, please wait...
                        </p>
                      </div>
                    ) : filteredParticipants.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        <i
                          className="mdi mdi-magnify-close"
                          style={{ fontSize: "2rem" }}
                        />
                        <p className="mt-2">
                          No participants match the current filters.
                        </p>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            this.setState({
                              colFilter_labName: "",
                              colFilter_roundsIssued: "",
                              colFilter_roundsParticipated: "",
                              colFilter_consecutiveRejections: "",
                              colFilter_coaStatus: "",
                              topFilter_labName: "",
                              topFilter_coaStatus: "",
                              topFilter_roundsParticipated: "",
                            })
                          }
                        >
                          Clear All Filters
                        </button>
                      </div>
                    ) : (
                      <BootstrapTable
                        keyField="lab_id"
                        data={filteredParticipants}
                        columns={columns}
                        bordered={false}
                        striped
                        wrapperClasses="participants-table"
                      />
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>

        {/* ── Analyte Rejection Details Modal ── */}
        <Modal
          isOpen={this.state.analytesModalOpen}
          toggle={() => this.setState({ analytesModalOpen: false })}
          size="xl"
        >
          <ModalHeader
            toggle={() => this.setState({ analytesModalOpen: false })}
          >
            Rejection Details — {this.state.selectedParticipantName}
          </ModalHeader>
          <ModalBody>
            {modalAnalytes.length === 0 ? (
              <p className="text-center text-muted">
                No analytes with 3 or more consecutive rejections found.
              </p>
            ) : (
              modalAnalytes.map((analyte, idx) => {
                const allRejectedSorted = (analyte.round_details || [])
                  .filter(rd => rd.evaluation === "Rejected")
                  .sort(
                    (a, b) =>
                      extractRoundNum(a.round_no) - extractRoundNum(b.round_no)
                  );

                const streakRounds =
                  getLongestConsecutiveStreak(allRejectedSorted);
                const displayRounds = [...streakRounds].sort(
                  (a, b) =>
                    extractRoundNum(b.round_no) - extractRoundNum(a.round_no)
                );

                return (
                  <div key={idx} style={{ marginBottom: "30px" }}>
                    <h5
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "8px 12px",
                        borderLeft: "4px solid #dc3545",
                        marginBottom: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      {analyte.analyte_name || "Unknown Analyte"}
                    </h5>
                    <table className="table table-bordered table-sm">
                      {this.state.selectedSchemeType === "Qualitative" ? (
                        <>
                          <thead
                            style={{
                              backgroundColor: "#343a40",
                              color: "#fff",
                            }}
                          >
                            <tr>
                              <th style={{ textAlign: "center" }}>Round No</th>
                              <th style={{ textAlign: "center" }}>
                                Instrument
                              </th>
                              <th style={{ textAlign: "center" }}>
                                Your Result
                              </th>
                              <th style={{ textAlign: "center" }}>
                                Expected Result
                              </th>
                              <th style={{ textAlign: "center" }}>Score</th>
                              <th style={{ textAlign: "center" }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayRounds.map((rd, rIdx) => (
                              <tr key={rIdx}>
                                <td style={{ textAlign: "center" }}>
                                  {rd.round_no}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.instrument || "—"}
                                </td>
                                <td
                                  style={{
                                    textAlign: "center",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {rd.your_result || "—"}
                                </td>
                                <td
                                  style={{
                                    textAlign: "center",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {rd.expected_result || "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.score === null || rd.score === undefined
                                    ? "—"
                                    : rd.score}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span
                                    style={{
                                      color:
                                        rd.evaluation === "Rejected"
                                          ? "red"
                                          : rd.evaluation === "Acceptable"
                                          ? "green"
                                          : rd.evaluation === "Warning"
                                          ? "orange"
                                          : "gray",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {rd.evaluation}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </>
                      ) : (
                        <>
                          <thead
                            style={{
                              backgroundColor: "#343a40",
                              color: "#fff",
                            }}
                          >
                            <tr>
                              <th style={{ textAlign: "center" }}>Round No</th>
                              <th style={{ textAlign: "center" }}>Unit</th>
                              <th style={{ textAlign: "center" }}>
                                Instrument
                              </th>
                              <th style={{ textAlign: "center" }}>Method</th>
                              <th style={{ textAlign: "center" }}>Reagent</th>
                              <th style={{ textAlign: "center" }}>Result</th>
                              <th style={{ textAlign: "center" }}>
                                Robust Mean
                              </th>
                              <th style={{ textAlign: "center" }}>Z-Score</th>
                              <th style={{ textAlign: "center" }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayRounds.map((rd, rIdx) => (
                              <tr key={rIdx}>
                                <td style={{ textAlign: "center" }}>
                                  {rd.round_no}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.unit || "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.instrument || "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.method || "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.reagent || "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.result ?? "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.robust_mean ?? "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {rd.z_score ?? "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span
                                    style={{
                                      color:
                                        rd.evaluation === "Rejected"
                                          ? "red"
                                          : rd.evaluation === "Acceptable"
                                          ? "green"
                                          : rd.evaluation === "Warning"
                                          ? "orange"
                                          : "gray",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {rd.evaluation}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </>
                      )}
                    </table>
                  </div>
                );
              })
            )}
          </ModalBody>
        </Modal>

        {/* ── Change Status Modal ── */}
        <Modal
          isOpen={this.state.changeStatusModalOpen}
          toggle={() => this.setState({ changeStatusModalOpen: false })}
          size="md"
          centered
        >
          <ModalHeader
            toggle={() => this.setState({ changeStatusModalOpen: false })}
          >
            <span style={{ color: "#556ee6", fontWeight: "bold" }}>
              Change COA Status
            </span>
          </ModalHeader>

          <ModalBody style={{ padding: "24px" }}>
            {currentParticipant && (
              <>
                <div
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginBottom: "20px",
                    borderLeft: "4px solid #556ee6",
                  }}
                >
                  <div style={{ fontWeight: "bold", fontSize: "15px" }}>
                    Participant: {currentParticipant.lab_name}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#555",
                      marginTop: "4px",
                    }}
                  >
                    Rounds Participated:{" "}
                    <strong>{currentParticipant.rounds_participated}</strong>
                    &nbsp;|&nbsp; Consecutive Rejections:{" "}
                    <strong style={{ color: "red" }}>
                      {getFailedAnalytesCount(
                        currentParticipant.analytes || []
                      )}
                    </strong>
                  </div>
                </div>

                <div style={{ marginBottom: "18px" }}>
                  <Label style={{ fontWeight: "600", marginBottom: "6px" }}>
                    Current Status
                  </Label>
                  <div>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 14px",
                        borderRadius: "20px",
                        fontSize: "13px",
                        fontWeight: "bold",
                        background: currentParticipant.coa_eligible
                          ? "#d4edda"
                          : "#f8d7da",
                        color: currentParticipant.coa_eligible
                          ? "#155724"
                          : "#721c24",
                        border: `1px solid ${
                          currentParticipant.coa_eligible
                            ? "#c3e6cb"
                            : "#f5c6cb"
                        }`,
                      }}
                    >
                      {currentParticipant.coa_eligible
                        ? "Eligible"
                        : "Not Eligible"}
                    </span>
                  </div>
                </div>

                <div style={{ marginBottom: "18px" }}>
                  <Label style={{ fontWeight: "600", marginBottom: "6px" }}>
                    Change Status To
                  </Label>
                  <Input
                    type="select"
                    value={this.state.newStatusValue}
                    onChange={e =>
                      this.setState({ newStatusValue: e.target.value })
                    }
                    style={{
                      borderRadius: "8px",
                      border: "1.5px solid #ced4da",
                      padding: "8px 12px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="eligible">Eligible</option>
                    <option value="not_eligible">Not Eligible</option>
                  </Input>
                </div>

                <div style={{ marginBottom: "8px" }}>
                  <Label style={{ fontWeight: "600", marginBottom: "6px" }}>
                    Reason{" "}
                    <span style={{ fontWeight: "normal", color: "#999" }}>
                      (optional)
                    </span>
                  </Label>
                  <Input
                    type="textarea"
                    rows={3}
                    placeholder="Enter reason for status change..."
                    value={this.state.statusChangeReason}
                    onChange={e =>
                      this.setState({ statusChangeReason: e.target.value })
                    }
                    style={{
                      borderRadius: "8px",
                      border: "1.5px solid #ced4da",
                      fontSize: "14px",
                      resize: "none",
                    }}
                  />
                </div>
              </>
            )}
          </ModalBody>

          <ModalFooter
            style={{ borderTop: "1px solid #eee", padding: "12px 24px" }}
          >
            <Button
              color="light"
              onClick={() => this.setState({ changeStatusModalOpen: false })}
              style={{ borderRadius: "8px", padding: "8px 20px" }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={this.handleStatusChange}
              style={{
                borderRadius: "8px",
                padding: "8px 24px",
                background: "#556ee6",
                border: "none",
              }}
            >
              <i className="mdi mdi-check me-1" />
              Confirm Change
            </Button>
          </ModalFooter>
        </Modal>
      </React.Fragment>
    );
  }
}

ReagentsList.propTypes = {
  match: PropTypes.object,
  className: PropTypes.any,
  schemeList: PropTypes.array,
  cycleList: PropTypes.array,
  coaData: PropTypes.array,
  schemeType: PropTypes.array,
  COACriteria: PropTypes.array,
  submittedCOAList: PropTypes.array, // ← NEW
  onGetCOACriteria: PropTypes.func,
  onGetSubmittedCOA: PropTypes.func, // ← NEW
  onGetSchemeList: PropTypes.func,
  onGetCycle: PropTypes.func,
  onGetScheme: PropTypes.func,
  onSubmitCOA: PropTypes.func,
  coaSubmit: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.string,
    response: PropTypes.any,
  }),
};

const mapStateToProps = state => ({
  schemeList: state.SchemeList?.SchemeList || [],
  cycleList: state.CycleList?.CycleList || [],
  coaData: state.SchemeList?.COACriteria || [],
  submittedCOAList: state.SchemeList?.submittedCOAList || [], // ← NEW
  coaSubmit: state.SchemeList?.coaSubmit || {
    loading: false,
    success: false,
    error: null,
  },
});

const mapDispatchToProps = dispatch => ({
  onGetScheme: id => dispatch(getSchemelist(id)),
  onGetCycle: id => dispatch(getcyclelist(id)),
  onGetCOACriteria: data => dispatch(COACriterialist(data)),
  onGetSubmittedCOA: data => dispatch(getCOASubmittedList(data)), // ← NEW
  onSubmitCOA: data => dispatch(submitCOAParticipants(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ReagentsList));
